import { triggerAssistantAction, ASSISTANT_ACTION_TYPES } from '../../utils/assistantActions';
import './CompanyConsultationNavigation.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaFilePdf, FaHistory, FaRecycle, FaSearch, FaSyncAlt, FaTimes, FaTrash, FaUndo, FaUsers } from 'react-icons/fa';
import api from '../../api';
import YoungApprenticeService from '../../services/YoungApprenticeService';
import { downloadYoungApprenticeTablePdf } from './youngApprenticeReportPdf';
import './YoungApprenticeCompanyTools.css';

const parseDate = (value, dateOnlyMode = false) => {
  if (!value) return null;
  const raw = String(value).trim();
  const candidate = dateOnlyMode && /^\d{4}-\d{2}-\d{2}/.test(raw)
    ? new Date(`${raw.slice(0, 10)}T00:00:00`)
    : new Date(raw);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};
const dateOnly = (value) => {
  const parsed = parseDate(value, true);
  return parsed ? parsed.toLocaleDateString('pt-BR') : '—';
};
const dateTime = (value) => {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleString('pt-BR') : '—';
};
const brl = (cents) => (Number(cents || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const statusLabel = (status) => ({ ACTIVE: 'Ativo', PLANNED: 'Planejado', ENDED: 'Encerrado', SENT: 'Enviado', FAILED: 'Falhou', RETRY: 'Nova tentativa', PROCESSING: 'Processando', PENDING: 'Pendente', QUEUED: 'Na fila' }[String(status || '').toUpperCase()] || status || '—');
const errorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;

const emailTypeLabel = (type) => ({ INVOICE: 'Fatura', VACATION: 'Férias', CONTRACT_END: 'Fim de contrato' }[type] || type || 'Não informado');
const emailStatusLabel = (status) => ({ SENT: 'Enviado ao servidor', UNKNOWN: 'Não informado' }[String(status || '').toUpperCase()] || (status ? statusLabel(status) : 'Não informado'));
const recipientText = (row) => Array.isArray(row.recipients) ? row.recipients.join(', ') : row.recipients || '—';

function PortalModal({ title, onClose, children, actions, className = '' }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="ya-company-tools__backdrop" role="dialog" aria-modal="true">
      <section className={`ya-company-tools__modal ${className}`}>
        <header><h2>{title}</h2><button type="button" onClick={onClose}><FaTimes /> Fechar</button></header>
        <div className="ya-company-tools__body">{children}</div>
        {actions && <footer>{actions}</footer>}
      </section>
    </div>,
    document.body,
  );
}

export function YoungApprenticeCompanyTrashButton({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const load = async () => {
    setLoading(true); setFeedback('');
    try { setRows(await YoungApprenticeService.listCompanyTrash()); }
    catch (error) { setFeedback(errorMessage(error, 'Não foi possível carregar a lixeira.')); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const restore = async (row) => {
    if (!window.confirm(`Restaurar a empresa “${row.tradeName || row.corporateName}”?`)) return;
    try { await YoungApprenticeService.restoreCompany(row.id); await load(); onChanged?.(); }
    catch (error) { setFeedback(errorMessage(error, 'Não foi possível restaurar a empresa.')); }
  };
  return <>
    <button type="button" className="young-apprentice-button young-apprentice-button--secondary" onClick={() => setOpen(true)}><FaRecycle /> Lixeira</button>
    {open && <PortalModal title="Lixeira de empresas" onClose={() => setOpen(false)}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      {loading ? <p>Carregando...</p> : rows.length ? <div className="ya-company-tools__list">{rows.map((row) => <article key={row.id}><div><strong>{row.tradeName || row.corporateName}</strong><small>{row.cnpj || 'CNPJ não informado'} · excluída em {dateTime(row.sourceMetadata?.trash?.deletedAt)}</small></div><button type="button" onClick={() => restore(row)}><FaUndo /> Restaurar</button></article>)}</div> : <p>Nenhuma empresa na lixeira.</p>}
    </PortalModal>}
  </>;
}

export default function YoungApprenticeCompanyTools({ company, onChanged, canManage = false }) {
 const [linkSearch,setLinkSearch]=useState('');
 const [linkStatus,setLinkStatus]=useState('ALL');
 const [linkPdfLoading,setLinkPdfLoading]=useState(false);
  const [panel, setPanel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  const [emailType, setEmailType] = useState('ALL');
  const [emailStatus, setEmailStatus] = useState('ALL');
  const requestVersion = useRef(0);
  useEffect(() => {
    requestVersion.current += 1;
    setPanel(null); setData(null); setFeedback(''); setLoading(false);
    return () => { requestVersion.current += 1; };
  }, [company?.id]);
  const filteredEmails = useMemo(() => {
    const term = emailSearch.trim().toLocaleLowerCase('pt-BR');
    return (Array.isArray(data) ? data : []).filter((row) => {
      if (emailType !== 'ALL' && row.type !== emailType) return false;
      if (emailStatus !== 'ALL' && String(row.deliveryStatus || 'UNKNOWN').toUpperCase() !== emailStatus) return false;
      return !term || [row.studentName, row.invoiceId, row.reference, recipientText(row), row.smtpMessageId, row.deliveryError, emailTypeLabel(row.type)]
        .some((value) => String(value || '').toLocaleLowerCase('pt-BR').includes(term));
    });
  }, [data, emailSearch, emailType, emailStatus]);

  const title = useMemo(() => company?.tradeName || company?.corporateName || 'Empresa', [company]);
  const filteredLinks = useMemo(() => {
    const term = linkSearch.trim().toLocaleLowerCase('pt-BR');
    return (data?.linked || []).filter((row) => {
      if (linkStatus !== 'ALL' && String(row.status || '').toUpperCase() !== linkStatus) return false;
      if (!term) return true;
      return [row.fullName, row.apprenticeNumber, row.role]
        .some((value) => String(value || '').toLocaleLowerCase('pt-BR').includes(term));
    });
  }, [data, linkSearch, linkStatus]);
  const loadPanel = async (type) => {
    const version = ++requestVersion.current;
    setData(null); setFeedback(''); setLoading(true);
    try {
      const result = type === 'links'
        ? await YoungApprenticeService.companyLinks(company.id)
        : await YoungApprenticeService.companyEmailHistory(company.id);
      if (version === requestVersion.current) setData(result);
    } catch (error) {
      if (version === requestVersion.current) setFeedback(errorMessage(error, 'Não foi possível carregar os dados.'));
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };
  const open = (type) => {
    setPanel(type);
    if (type === 'links') { setLinkSearch(''); setLinkStatus('ALL'); }
    if (type === 'emails') { setEmailSearch(''); setEmailType('ALL'); setEmailStatus('ALL'); }
    loadPanel(type);
  };

  const remove = async () => {
    requestVersion.current += 1;
    setLoading(true); setFeedback('');
    try {
      const links = await YoungApprenticeService.companyLinks(company.id);
      if (links.current?.length) {
        setData(links); setPanel('delete-linked'); return;
      }
      if (!window.confirm(`Confirma a exclusão da empresa “${title}”? Ela será enviada para a lixeira.`)) return;
      await YoungApprenticeService.deleteCompany(company.id);
      setPanel(null); onChanged?.();
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível excluir a empresa.')); setPanel('delete-linked'); }
    finally { setLoading(false); }
  };

  const unlinkAndDelete = async () => {
    const count = Number(data?.current?.length || 0);
    if (!window.confirm(`Esta empresa possui ${count} jovem(ns) vinculado(s). Ao continuar, o vínculo atual será encerrado, o campo Empresa ficará vazio para esses jovens e a alteração será enviada ao Conere. Deseja continuar?`)) return;
    setLoading(true); setFeedback('');
    try {
      await YoungApprenticeService.deleteCompany(company.id, { unlink: true });
      setPanel(null); onChanged?.();
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível desvincular os jovens e excluir a empresa.')); }
    finally { setLoading(false); }
  };

  const downloadLinksPdf = async () => {
    setLinkPdfLoading(true); setFeedback('');
    try {
      const organization = await api.get('/company').then((response) => response.data).catch(() => null);
      await downloadYoungApprenticeTablePdf({
        title: 'Vínculos da Empresa',
        subtitle: title,
        organization,
        rows: filteredLinks,
        columns: [
          { key: 'fullName', label: 'Jovem', value: (row) => row.fullName || '—', weight: 1.7 },
          { key: 'apprenticeNumber', label: 'Nº aprendiz', value: (row) => row.apprenticeNumber || '—' },
          { key: 'status', label: 'Status', value: (row) => statusLabel(row.status) },
          { key: 'startDate', label: 'Início', value: (row) => dateOnly(row.startDate) },
          { key: 'endDate', label: 'Fim', value: (row) => dateOnly(row.endDate) },
        ],
        filters: [linkSearch.trim() ? `Busca: ${linkSearch.trim()}` : '', linkStatus !== 'ALL' ? `Status: ${statusLabel(linkStatus)}` : 'Todos os status'].filter(Boolean).join(' • '),
        filename: `vinculos-${String(title).normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (error) { setFeedback(errorMessage(error, 'Não foi possível gerar o PDF dos vínculos.')); }
    finally { setLinkPdfLoading(false); }
  };

  const openStudent = row => {
    const id = Number(row.studentId);
    if (!Number.isSafeInteger(id) || id <= 0) return;
    triggerAssistantAction({ type: ASSISTANT_ACTION_TYPES.OPEN_TAB, tabId: 'student-management', tabProps: { initialStudentId: id, initialStudentName: row.fullName, initialStudentRequest: `${Date.now()}-${row.employmentId}` } });
    close();
  };
  const close = () => { requestVersion.current += 1; setPanel(null); setData(null); setFeedback(''); setLoading(false); };
  return <>
    <div className="ya-company-tools__actions">
      <button type="button" title="Ver jovens vinculados" onClick={() => open('links')}><FaUsers /><span>Vínculos</span></button>
      <button type="button" title="Histórico de envios da empresa" onClick={() => open('emails')}><FaHistory /><span>Envios</span></button>
      {canManage && <button type="button" className="danger" title="Excluir empresa" onClick={remove}><FaTrash /><span>Excluir</span></button>}
    </div>

    {panel === 'links' && <PortalModal title={`Jovens vinculados — ${title}`} onClose={close}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      {!loading && data?.linked?.length > 0 && <div className="ya-company-links-toolbar"><label><FaSearch /><input value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} placeholder="Filtrar por jovem, nº aprendiz ou função" /></label><select value={linkStatus} onChange={(e) => setLinkStatus(e.target.value)}><option value="ALL">Todos os vínculos</option><option value="ACTIVE">Ativos</option><option value="PLANNED">Planejados</option><option value="ENDED">Encerrados</option></select><button type="button" onClick={downloadLinksPdf} disabled={linkPdfLoading}><FaFilePdf /> {linkPdfLoading ? 'Gerando...' : 'Gerar PDF'}</button></div>}
      {loading ? <p>Carregando...</p> : data?.linked?.length ? <div className="ya-company-tools__table"><table><thead><tr><th>Jovem</th><th>Nº aprendiz</th><th>Status</th><th>Início</th><th>Fim</th></tr></thead><tbody>{filteredLinks.length ? filteredLinks.map((row) => <tr key={row.employmentId} onDoubleClick={event => { if (!event.target.closest('button,a,input,select,textarea,label')) openStudent(row); }}><td><button type="button" className="ya-consultation-name" title="Abrir ficha do jovem" onClick={event => { event.stopPropagation(); if (event.detail < 2) openStudent(row); }}>{row.fullName}</button></td><td>{row.apprenticeNumber || '—'}</td><td>{statusLabel(row.status)}</td><td>{dateOnly(row.startDate)}</td><td>{dateOnly(row.endDate)}</td></tr>) : <tr><td colSpan="5">Nenhum vínculo encontrado com os filtros informados.</td></tr>}</tbody></table></div> : <p>Nenhum jovem vinculado a esta empresa.</p>}
      {!loading && canManage && data?.current?.length > 0 && <div className="ya-company-tools__warning">Há {data.current.length} vínculo(s) atual(is). A exclusão da empresa só é permitida após desvincular esses jovens.</div>}
    </PortalModal>}

    {panel === 'emails' && <PortalModal title={`Histórico de envios — ${title}`} onClose={close} className="ya-company-email-history">
      <p className="ya-company-email-history__note">Faturas, avisos de férias e de fim de contrato. “Enviado ao servidor” indica aceite pelo servidor de e-mail. A entrega na caixa de entrada e a leitura não têm confirmação.</p>
      <div className="ya-company-email-history__toolbar">
        <label><FaSearch /><input aria-label="Buscar envios" value={emailSearch} onChange={(event) => setEmailSearch(event.target.value)} placeholder="Jovem, fatura, destinatário ou erro" /></label>
        <select aria-label="Tipo de envio" value={emailType} onChange={(event) => setEmailType(event.target.value)}><option value="ALL">Todos os tipos</option><option value="INVOICE">Fatura</option><option value="VACATION">Férias</option><option value="CONTRACT_END">Fim de contrato</option></select>
        <select aria-label="Status do envio" value={emailStatus} onChange={(event) => setEmailStatus(event.target.value)}><option value="ALL">Todos os status</option>{['SENT', 'QUEUED', 'PENDING', 'PROCESSING', 'RETRY', 'FAILED', 'UNKNOWN'].map((status) => <option key={status} value={status}>{status === 'UNKNOWN' ? 'Não informado' : emailStatusLabel(status)}</option>)}</select>
        <button type="button" disabled={loading} onClick={() => loadPanel('emails')}><FaSyncAlt /> Atualizar</button>
      </div>
      {feedback && <div role="alert" className="ya-company-tools__feedback">{feedback}</div>}
      {loading ? <p role="status">Carregando envios...</p> : Array.isArray(data) && data.length ? <>
        <p className="ya-company-email-history__count">{filteredEmails.length} de {data.length} registro(s)</p>
        <div className="ya-company-tools__table"><table><thead><tr><th>Datas</th><th>Tipo e referência</th><th>Destinatários</th><th>Envio e confirmação</th><th>Tentativas e retorno</th></tr></thead><tbody>{filteredEmails.length ? filteredEmails.map((row) => <tr key={row.id}>
          <td><strong>Criado: {dateTime(row.createdAt)}</strong>{row.sentAt && <small>Aceito pelo servidor: {dateTime(row.sentAt)}</small>}{row.deliveryUpdatedAt && <small>Atualizado: {dateTime(row.deliveryUpdatedAt)}</small>}{row.dueDate && <small>Data de referência: {dateOnly(row.dueDate)}</small>}</td>
          <td><strong>{emailTypeLabel(row.type)}</strong>{row.studentName && <small>Jovem: {row.studentName}</small>}{row.invoiceId && <small>Fatura #{row.invoiceId} · {row.reference || '—'} · {brl(row.totalCents)}</small>}{row.changedByName && <small>Registrado por: {row.changedByName}</small>}</td>
          <td className="ya-company-email-history__recipients">{recipientText(row)}</td>
          <td><span className={`ya-company-tools__status ya-company-tools__status--${String(row.deliveryStatus || '').toLowerCase()}`}>{emailStatusLabel(row.deliveryStatus) || 'Não informado'}</span><small>Entrega sem confirmação</small><small>Leitura sem confirmação</small></td>
          <td><span>Tentativas com falha: {row.deliveryAttempts ?? 'Não informado'}</span>{row.lastAttemptAt && <small>Última tentativa: {dateTime(row.lastAttemptAt)}</small>}{row.nextRetryAt && <small>Próxima tentativa: {dateTime(row.nextRetryAt)}</small>}{row.smtpMessageId && <small className="ya-company-email-history__smtp">ID SMTP: {row.smtpMessageId}</small>}{row.deliveryError && <small className="ya-company-email-history__error">Erro: {row.deliveryError}</small>}{!row.smtpMessageId && !row.deliveryError && <small>Sem retorno do servidor registrado.</small>}</td>
        </tr>) : <tr><td colSpan="5">Nenhum envio encontrado com os filtros informados.</td></tr>}</tbody></table></div>
      </> : !feedback && <p>Nenhum envio registrado para esta empresa.</p>}
    </PortalModal>}

    {panel === 'delete-linked' && <PortalModal title={`Não é possível excluir “${title}” ainda`} onClose={close} actions={<button type="button" className="danger" disabled={loading} onClick={unlinkAndDelete}><FaTrash /> Desvincular jovens e enviar empresa para lixeira</button>}>
      {feedback && <div className="ya-company-tools__feedback">{feedback}</div>}
      <div className="ya-company-tools__warning"><strong>{Number(data?.current?.length || 0)} jovem(ns) possuem vínculo atual com esta empresa.</strong><br />Para excluir, o iBPM encerrará esses vínculos, deixará o campo Empresa em branco no cadastro atual dos jovens e enviará a atualização ao Conere.</div>
      {!!data?.current?.length && <ul>{data.current.map((row) => <li key={row.employmentId}>{row.fullName}</li>)}</ul>}
    </PortalModal>}
  </>;
}
